import { ApiShopperLandingPage } from '@storefronts/common-types'
import './content-sections/RegisterSections'
import dynamic from 'next/dynamic'
import {
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
} from 'react'
import { getLandingPageProducts } from '../../../api'
import { AnalyticsContext } from '../../context/analyticsContext'
import { CustomizationContextProvider } from '../../context/CustomizationContextProvider'
import { OverrideContext, OverrideInput } from '../../context/overrideContext'
import { PageContextProvider } from '../../context/PageContextProvider'
import {
    IS_EDITOR_PREVIEW,
    QueryParamContext,
    QueryParamContextProvider,
} from '../../context/QueryParamContextProvider'
import { LandingPageLinksProvider } from '../../context/LandingPageLinksContext'
import { UserEventDataContextProvider } from '../../context/UserEventDataContextProvider'
import { ViddyLPContext } from '../../context/viddyLPContext'
import { initializeViddyId } from '../../storage'
import { applyHydratedLandingPageProducts } from '../../utils/hydrateLandingPageProducts'
import { KlaviyoLogger } from '../pixels/KlaviyoPixel'
import { AnalyticsProvider } from '../shared/AnalyticsProvider'
import PersonalizedLP from './experiences/PersonalizedLP'
import { LandingPageModel } from '../../../models/landingPageModel'
import { ConsentModal } from '../consent/ConsentModal'
import {
    useConsentTrackingGate,
    useTrackingAllowed,
} from '../consent/consentTrackingStore'

const PreviewBanner = dynamic(
    () =>
        import('../PreviewBanner').then((mod) => ({
            default: mod.PreviewBanner,
        })),
    { ssr: false },
)

export const LandingPage: React.FC<{
    landingPage: ApiShopperLandingPage
}> = ({ landingPage }) => {
    return (
        <AnalyticsProvider resource={landingPage}>
            <LandingPageContextWrapper
                landingPage={landingPage}
                hydrateProducts
            />
        </AnalyticsProvider>
    )
}

// Broke out to export
export const LandingPageContextWrapper: React.FC<{
    landingPage: ApiShopperLandingPage
    overrides?: OverrideInput
    displayPoweredByFooter?: boolean
    hydrateProducts?: boolean
}> = ({ landingPage, overrides, displayPoweredByFooter, hydrateProducts }) => {
    // Priority: explicit override > clientSettings > default true
    const shouldDisplayFooter =
        displayPoweredByFooter ??
        landingPage.settings?.displayPoweredByFooter ??
        true

    return (
        <OverrideContext.Provider
            value={{
                qpOverrides: overrides?.qpOverrides,
                mainContentOverride: overrides?.mainContentOverride,
                selectedBlockIdx: overrides?.selectedBlockIdx,
                selectedBlockScope: 'LANDING_PAGE',
                displayPoweredByFooter: shouldDisplayFooter,
                dynamicGridProducts: overrides?.dynamicGridProducts,
            }}
        >
            <QueryParamContextProvider landingPage={landingPage}>
                <CustomizationContextProvider landingPage={landingPage}>
                    <LandingPageContextProvider
                        landingPage={landingPage}
                        hydrateProducts={hydrateProducts}
                    >
                        <UserEventDataContextProvider>
                            <LandingPageLinksProvider
                                dynamicGridProducts={
                                    overrides?.dynamicGridProducts
                                }
                            >
                                <PageContextProvider>
                                    <ConsentModal />
                                    <PreviewBanner />
                                    <ExperienceController />
                                </PageContextProvider>
                            </LandingPageLinksProvider>
                        </UserEventDataContextProvider>
                    </LandingPageContextProvider>
                </CustomizationContextProvider>
            </QueryParamContextProvider>
        </OverrideContext.Provider>
    )
}

const LandingPageContextProvider: React.FC<{
    children: React.ReactNode
    landingPage: ApiShopperLandingPage
    hydrateProducts?: boolean
}> = ({ children, landingPage, hydrateProducts }) => {
    const { DISCOUNT_CODE, IS_PREVIEW } = useContext(QueryParamContext)
    const trackingAllowed = useConsentTrackingGate(
        landingPage.settings?.clientConsentSetting,
        IS_PREVIEW || IS_EDITOR_PREVIEW(),
    )
    const isClient = useSyncExternalStore(
        () => () => {},
        () => true,
        () => false,
    )
    const viddyId = isClient && trackingAllowed ? initializeViddyId() : ''

    const [hydratedOverride, setHydratedOverride] =
        useState<ApiShopperLandingPage | null>(null)
    const pageForModel =
        hydrateProducts &&
        hydratedOverride != null &&
        hydratedOverride.landingPageHash === landingPage.landingPageHash
            ? hydratedOverride
            : landingPage

    const landingPageModel = useMemo(
        () => LandingPageModel.from(pageForModel),
        [pageForModel],
    )

    const lpCtxStore: ViddyLPContext = {
        landingPage: landingPageModel,
        viddyId,
        discountCode: DISCOUNT_CODE || landingPageModel.discountCode || null,
    }

    useEffect(() => {
        if (!hydrateProducts) return
        let cancelled = false
        getLandingPageProducts(landingPage.landingPageHash)
            .then((payload) => {
                if (cancelled || payload == null) return
                setHydratedOverride(
                    applyHydratedLandingPageProducts(landingPage, payload),
                )
            })
            .catch(() => undefined)
        return () => {
            cancelled = true
        }
    }, [hydrateProducts, landingPage])

    return (
        <ViddyLPContext.Provider value={lpCtxStore}>
            {children}
        </ViddyLPContext.Provider>
    )
}

const ExperienceController: React.FC = () => {
    const { landingPage, viddyId } = useContext(ViddyLPContext)
    const { Analytics } = useContext(AnalyticsContext)
    const trackingAllowed = useTrackingAllowed()
    const impressionFired = useRef(false)

    useEffect(() => {
        if (!trackingAllowed) return
        const timeouts = [
            setTimeout(() => {
                Analytics.TRACK_LANDING_PAGE_N_SECOND_LOAD(1)
            }, 1000),
            setTimeout(() => {
                Analytics.TRACK_LANDING_PAGE_N_SECOND_LOAD(3)
            }, 3000),
            setTimeout(() => {
                Analytics.TRACK_LANDING_PAGE_N_SECOND_LOAD(5)
            }, 5000),
        ]
        return () => {
            timeouts.forEach((timeout) => clearTimeout(timeout))
        }
    }, [trackingAllowed])

    useEffect(() => {
        if (impressionFired.current || !viddyId) return
        Analytics.TRACK_LANDING_PAGE_IMPRESSION(landingPage.ux, viddyId)
        KlaviyoLogger.TrackVLPImpression(landingPage)
        impressionFired.current = true
    }, [viddyId])

    return <PersonalizedLP />
}
